/**
 * Префикс всех записей, принадлежащих кэшу конфигураций представлений.
 */
export const STORAGE_PREFIX = 'view_config_';

/**
 * Структура записи в localStorage с поддержкой TTL.
 */
interface CacheEntry<T> {
  /** Сохраняемые данные. */
  data: T;
  /** Время создания записи. */
  timestamp: number;
  /** Абсолютное время окончания действия записи. */
  expiresAt: number;
}

/**
 * Сохраняет значение в localStorage с указанным TTL.
 */
export function setWithTTL<T>(key: string, value: T, ttlMs: number): void {
  const now = Date.now();

  const entry: CacheEntry<T> = {
    data: value,
    timestamp: now,
    expiresAt: now + ttlMs,
  };

  localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
}

/**
 * Получает значение из localStorage с проверкой TTL.
 * Просроченная или повреждённая запись автоматически удаляется.
 */
export function getWithTTL<T>(key: string, ttlMs: number): T | null {
  const storageKey = STORAGE_PREFIX + key;
  const raw = localStorage.getItem(storageKey);

  if (!raw) {
    return null;
  }

  try {
    const entry: CacheEntry<T> = JSON.parse(raw);

    const expiresAt = entry.expiresAt ?? entry.timestamp + ttlMs;

    if (Date.now() >= expiresAt) {
      localStorage.removeItem(storageKey);
      return null;
    }

    return entry.data;
  } catch {
    localStorage.removeItem(storageKey);
    return null;
  }
}

/**
 * Удаляет одну запись из кэша.
 */
export function removeFromStorage(key: string): void {
  localStorage.removeItem(STORAGE_PREFIX + key);
}

/**
 * Полностью очищает кэш конфигураций.
 */
export function clearStorage(): void {
  const keys = Object.keys(localStorage).filter((key) => key.startsWith(STORAGE_PREFIX));

  keys.forEach((key) => {
    localStorage.removeItem(key);
  });
}

/**
 * Удаляет записи кэша по префиксу логического ключа.
 */
export function clearStorageByPrefix(prefix: string): void {
  const fullPrefix = STORAGE_PREFIX + prefix;

  const keys = Object.keys(localStorage).filter((key) => key.startsWith(fullPrefix));

  keys.forEach((key) => {
    localStorage.removeItem(key);
  });
}
